import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import companyLogo from './../assets/images/logo.png';
import AuthService from '../services/AuthService';
import styles from './../styles/AuthPages.module.css';

const cx = (...names) => names.map((name) => styles[name]).join(' ');

const getQueryValue = (params, keys) => {
  for (const key of keys) {
    const value = params.get(key);
    if (value) {
      return value;
    }
  }

  return '';
};

const passwordChecks = (password) => [
  { id: 'length', label: 'At least 12 characters', passed: password.length >= 12 },
  { id: 'upper', label: 'One uppercase letter', passed: /[A-Z]/.test(password) },
  { id: 'lower', label: 'One lowercase letter', passed: /[a-z]/.test(password) },
  { id: 'number', label: 'One number', passed: /\d/.test(password) },
  { id: 'special', label: 'One special character', passed: /[^A-Za-z0-9]/.test(password) },
];

function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const currentSearch = location.search;
    const storageKey = 'reset-password-query';

    if (currentSearch) {
      sessionStorage.setItem(storageKey, currentSearch);
      return;
    }

    const savedSearch = sessionStorage.getItem(storageKey);
    if (savedSearch) {
      navigate(`${location.pathname}${savedSearch}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const effectiveParams = useMemo(() => {
    if (location.search) {
      return searchParams;
    }

    const savedSearch = sessionStorage.getItem('reset-password-query');
    return new URLSearchParams(savedSearch || '');
  }, [location.search, searchParams]);

  const token = getQueryValue(effectiveParams, ['token', 'code', 'resetToken']);
  const uid = getQueryValue(effectiveParams, ['uid']);
  const tid = getQueryValue(effectiveParams, ['tid']);
  const email = getQueryValue(effectiveParams, ['email']);
  const userId = getQueryValue(effectiveParams, ['userId', 'id']) || uid;
  const tokenId = getQueryValue(effectiveParams, ['tokenId']) || tid;
  const checks = useMemo(() => passwordChecks(password), [password]);
  const matches = !!confirmPassword && password === confirmPassword;
  const isValid = checks.every((check) => check.passed) && matches;
  const hasResetContext = !!token && !!userId && !!tokenId;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!hasResetContext) {
      setError('This reset link is missing the required user or token details.');
      return;
    }

    if (!isValid) {
      setError('Choose a stronger password and make sure both fields match.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await AuthService.resetForgottenPassword({
        userId,
        tokenId,
        token,
        newPassword: password,
      });

      if (!response?.isSuccess) {
        setError(response?.message || 'Unable to reset password.');
        return;
      }

      setSuccess(response.message || 'Your password has been reset successfully.');
      setPassword('');
      setConfirmPassword('');
    } catch (submitError) {
      setError(submitError?.message || 'Unable to reset password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles['reset-password-page']}>
      <section className={styles['reset-password-shell']}>
        <div className={cx('reset-password-panel', 'reset-password-brand')}>
          <div className={styles['reset-password-brand-top']}>
            <img src={companyLogo} alt="Company Logo" className={styles['reset-password-logo']} />
            <span className={styles['reset-password-eyebrow']}>Secure reset</span>
          </div>

          <div className={styles['reset-password-brand-copy']}>
            <h1>Reset your password</h1>
            <p>
              We received a request to reset your password. This email link is time-limited, so
              set a new password now to regain access to your account.
            </p>
          </div>

          <div className={styles['reset-password-brand-notes']}>
            <div>
              <strong>Hi</strong>
              <span>{uid || email || 'Password reset request detected.'}</span>
            </div>
            <div>
              <strong>Link expiry</strong>
              <span>
                {hasResetContext
                  ? 'This reset link appears valid and expires in about 15 minutes.'
                  : 'This link looks incomplete. Request a fresh password reset email.'}
              </span>
            </div>
          </div>
        </div>

        <form className={cx('reset-password-panel', 'reset-password-card')} onSubmit={handleSubmit}>
          <div className={styles['reset-password-header']}>
            <div>
              <h2>Choose a new password</h2>
              <p>
                {uid || email || userId
                  ? `Resetting password for ${uid || email || userId}.`
                  : 'Enter your new password below.'}
              </p>
            </div>
            <Link to="/login" className={styles['reset-password-link']}>
              Back to login
            </Link>
          </div>

          {error && <div className={cx('reset-password-alert', 'error')}>{error}</div>}
          {success && <div className={cx('reset-password-alert', 'success')}>{success}</div>}

          {!success && (
            <>
              <label className={styles['reset-password-field']}>
                <span>New password</span>
                <div className={styles['reset-password-input-row']}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter your new password"
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className={styles['reset-password-toggle']}
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </label>

              <label className={styles['reset-password-field']}>
                <span>Confirm new password</span>
                <div className={styles['reset-password-input-row']}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Confirm your new password"
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className={styles['reset-password-toggle']}
                    onClick={() => setShowConfirmPassword((value) => !value)}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </label>

              <div className={styles['reset-password-help']}>
                <h3>Password requirements</h3>
                <div className={styles['reset-password-checks']}>
                  {checks.map((check) => (
                    <div
                      key={check.id}
                      className={cx('reset-password-check', check.passed ? 'passed' : '')}
                    >
                      <span className={styles['reset-password-check-indicator']}>{check.passed ? '✓' : '•'}</span>
                      <span>{check.label}</span>
                    </div>
                  ))}
                  <div className={cx('reset-password-check', matches ? 'passed' : '')}>
                    <span className={styles['reset-password-check-indicator']}>{matches ? '✓' : '•'}</span>
                    <span>Passwords match</span>
                  </div>
                </div>
              </div>

              <div className={styles['reset-password-actions']}>
                <Link to="/login" className={styles['reset-password-secondary']}>
                  Cancel
                </Link>
                <button
                  type="submit"
                  className={styles['reset-password-primary']}
                  disabled={isSubmitting || !hasResetContext}
                >
                  {isSubmitting ? 'Updating...' : 'Reset password'}
                </button>
              </div>
            </>
          )}

          {success && (
            <div className={styles['reset-password-success-actions']}>
              <Link to="/login" className={styles['reset-password-primary']}>
                Continue to login
              </Link>
            </div>
          )}
        </form>
      </section>
    </div>
  );
}

export default ResetPasswordPage;
